import React from 'react';
import { useTranslation } from 'react-i18next';

// ===============================
// INTERFACE DEL MODAL
// ===============================

interface ModalConfirmarEliminarProps {
  isOpen: boolean;
  titulo: string;
  mensaje: string;
  detalles?: string[];
  onConfirmar: () => void;
  onCancelar: () => void;
  confirmText?: string;
  cancelText?: string;
  tipo?: 'danger' | 'warning' | 'info';
  loading?: boolean;
}

// ===============================
// COMPONENTE MODAL
// ===============================

export const ModalConfirmarEliminar: React.FC<ModalConfirmarEliminarProps> = ({
  isOpen,
  titulo,
  mensaje,
  detalles = [],
  onConfirmar,
  onCancelar,
  confirmText,
  cancelText,
  tipo = 'danger'
}) => {
  const { t } = useTranslation();
  const resolvedConfirmText = confirmText ?? t('common.delete');
  const resolvedCancelText = cancelText ?? t('common.cancel');

  if (!isOpen) return null;

  const getIcon = () => {
    switch (tipo) {
      case 'danger':
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
            <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
          </div>
        );
    }
  };

  const getButtonColors = () => {
    switch (tipo) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500';
      default:
        return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onCancelar}
        />
        
        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              {getIcon()}
              
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <h3 className="text-base font-semibold leading-6 text-gray-900">
                  {titulo}
                </h3>
                
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    {mensaje}
                  </p>
                  
                  {detalles.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {detalles.map((detalle, index) => (
                        <p key={index} className="text-xs text-gray-600">
                          • {detalle}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto ${
                getButtonColors()
              }`}
              onClick={onConfirmar}
            >
              {resolvedConfirmText}
            </button>
            
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              onClick={onCancelar}
            >
              {resolvedCancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalConfirmarEliminar;